import { Card, CardBody, Col, Container, Input, Label, Row } from 'reactstrap'
import DataTable, { TableColumn } from "react-data-table-component";
import { useEffect, useMemo, useState } from 'react';
import { SearchTableButton } from '../../../../utils/Constant';
import api from '../../../../utils/api';
import { CardRechargeTableDataColumnType } from '../../../../Types/Application/Ecommerce/Ecommerce';
import Swal from 'sweetalert2';
import { AllCardAssignTableProductName } from '../../../../Data/Application/Ecommerce/AllCardAssign';
import RechargeHistoryFilterHeader from '../../../../Componant/Application/Ecommerce/ProductList/RechargeHistoryFilterHeader';
import { AllRechargeTableAction } from '../../../../Data/Application/Ecommerce/RechargeHistory';

const RechargeHistoryContainer = () => {
  const [filterText, setFilterText] = useState("");
  const [cardAssignTableDatas, setCardAssignTableDatas] = useState([]);
  
  // ⬇️ PAGINATION STATE VARIABLES
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  // ⬇️ UPDATED FETCH FUNCTION WITH PAGINATION
  const fetchAllRecharge = async (page: number, size: number) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/CardRecharge/GetAllCardRechargePagination?pageNumber=${page}&pageSize=${size}`
      );
      if (response.status === 200) {
        // ⬇️ response.data.items use karein (assuming same structure)
        setCardAssignTableDatas(
          response.data.items.map((item: any): CardRechargeTableDataColumnType => ({
            cardRechargeCalculationId: item.cardRechargeCalculationId,
            cardRechargeAmount: item.cardRechargeAmount,
            ticketRFIdRef: item.ticketRFIdRef,
          }))
        );
        setTotalRows(response.data.totalCount); // ⬅️ Total count set karein
      }
    } catch (error) {
      console.error("Failed to fetch recharge history:", error);
      Swal.fire("Error!", "Failed to fetch recharge history.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ⬇️ USEEFFECT WITH PAGINATION DEPENDENCIES
  useEffect(() => {
    fetchAllRecharge(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this record!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        confirmDelete(id);
      }
    });
  };

  const confirmDelete = async (id: number) => {
    try {
      await api.delete(
        `/api/CardRecharge/DeleteCardRechargeById?cardRechargeCalculationId=${id}`
      );
      
      // ⬇️ REFRESH CURRENT PAGE
      await fetchAllRecharge(currentPage, pageSize);
      
      Swal.fire('Deleted!', 'Record has been deleted.', 'success');
    } catch (error) {
      Swal.fire('Error!', 'Failed to delete the record.', 'error');
      console.error("Delete error:", error);
    }
  };

  const allRechargeTableDataColumn: TableColumn<CardRechargeTableDataColumnType>[] = [
    {
      name: "Ticket ID",
      cell: (row) => <AllCardAssignTableProductName title={row.ticketRFIdRef} />,
      sortable: true,
      grow: 2,
    },
    {
      name: "Amount",
      selector: (row) => `${row.cardRechargeAmount}`,
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <AllRechargeTableAction 
          cardRechargeCalculationId={row.cardRechargeCalculationId} 
          onDelete={handleDelete} 
        />
      ),
    },
  ];


  const filteredItems = cardAssignTableDatas.filter((item: any) => {
    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(search)
    );
  });

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="dataTables_filter d-flex align-items-center">
        <Label className="me-2">{SearchTableButton}:</Label>
        <Input 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)} 
          type="search" 
          value={filterText} 
        />
      </div>
    );
  }, [filterText]);

  // ⬇️ PAGE CHANGE HANDLERS
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  return (
    <Container fluid>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <div className="list-product-header">
                <RechargeHistoryFilterHeader />
              </div>
              <div className="list-product">
                <div className="table-responsive">
                  {/* ⬇️ UPDATED DATATABLE WITH SERVER PAGINATION */}
                  <DataTable 
                    data={filteredItems} // Ya direct cardAssignTableDatas if no local search
                    columns={allRechargeTableDataColumn} 
                    striped 
                    highlightOnHover 
                    pagination
                    paginationServer // ⬅️ SERVER-SIDE PAGINATION ENABLE
                    paginationTotalRows={totalRows} // ⬅️ TOTAL ROWS FROM API
                    onChangePage={handlePageChange} // ⬅️ PAGE CHANGE HANDLER
                    onChangeRowsPerPage={handlePerRowsChange} // ⬅️ ROWS PER PAGE HANDLER
                    paginationDefaultPage={currentPage} // ⬅️ CURRENT PAGE
                    paginationPerPage={pageSize} // ⬅️ PAGE SIZE
                    progressPending={loading} // ⬅️ LOADING INDICATOR
                    selectableRows 
                    subHeader 
                    subHeaderComponent={subHeaderComponentMemo} 
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default RechargeHistoryContainer